import { api } from 'utils';
import {
    POST_FIELDS, PAGE_FIELDS, TAG_FIELDS,
    CATEGORY_CONTENT, CATEGORY_EVENTS, CATEGORY_CONTENT_MENU,
    CATEGORY_CONTENT_DETAIL, CATEGORY_LOGO_DETAIL,
    getTagsAll, getCategoriesAll, getCategoryColors, getContext,
    mapPost, mapPage, mapPostDetail
} from './wp';

export const getWPJSON = async () => {
    return await api.get('/wp/v2');
}

export const getMenu = async () => {
    const [categories, colors] = await Promise.all([getCategoriesAll(), getCategoryColors()]);
    const allTags = await getTagsAll();

    const menu = categories
        .filter(c => c.parent === CATEGORY_CONTENT)
        .filter(c => c.id !== CATEGORY_CONTENT_MENU && c.id !== CATEGORY_CONTENT_DETAIL)
        .filter(c => c.count > 1 && c.name.indexOf('- Menu') === -1)
        .map(c => ({ id: c.id, name: c.name, slug: c.slug, color: colors[c.id] || '', listTags: [] }));

    menu.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));

    menu.unshift({ id: CATEGORY_CONTENT, name: 'Tudo', slug: 'tudo', color: colors[CATEGORY_CONTENT] || '#212121', listTags: [] });

    await Promise.all(menu.map(async (item) => {
        const { data: posts } = await api.get('/wp/v2/posts', {
            params: { categories: item.id, per_page: 100, _fields: 'tags' }
        });
        const tagIds = [...new Set(posts.flatMap(p => p.tags || []))];
        item.listTags = allTags.filter(t => tagIds.includes(t.id)).map(t => ({ id: t.id, name: t.name }));
    }));

    return { data: menu };
}

// Lista de conteúdos (Content/GetContent) — API nativa + mesmo formato do core.
export const getPosts = async (params = {}) => {
    const { category, tags, page = 1, perpage = 20 } = params;
    const base = {
        per_page: perpage,
        page,
        _fields: POST_FIELDS,
        order: 'desc',
        orderby: 'date'
    };
    if (category) base.categories = category;
    if (tags && tags.length) base.tags = tags.join(',');

    // Sticky só existe na primeira página (não aceita page>1 — a API retorna 400).
    let sticky = [];
    if (page === 1) {
        try {
            const { data } = await api.get('/wp/v2/posts', { params: { ...base, sticky: 1, page: 1 } });
            sticky = data;
        } catch (e) {
            sticky = [];
        }
    }
    const { data: rest } = await api.get('/wp/v2/posts', { params: base });

    const posts = [...sticky, ...rest.filter(p => !p.sticky && !sticky.some(s => s.id === p.id))]
        .filter(p => !p.categories.includes(CATEGORY_CONTENT_DETAIL) && !p.categories.includes(CATEGORY_LOGO_DETAIL));

    const { categories, colors } = await getContext();
    return { data: await Promise.all(posts.map(p => mapPost(p, categories, colors))) };
}

export const getPostBySlug = async (slug) => {
    const { data } = await api.get('/wp/v2/posts', {
        params: { slug, _fields: POST_FIELDS }
    });
    return { data: data.length ? await mapPostDetail(data[0]) : null };
}

// Content/GetRelated — replica o fluxo do core: brand (por slug) → tags → categoria.
export const getPostsRelated = async (exclude, tags, category, brandSlug, numberOfRelated = 2, brandsOnly = false) => {
    const limit = numberOfRelated || 2;
    const postsResult = [];
    const seen = new Set();
    const { categories, colors } = await getContext();

    const baseParams = (cats) => ({
        per_page: limit + 1,
        _fields: POST_FIELDS,
        categories: cats.join(','),
        order: 'desc',
        orderby: 'id'
    });

    const pushPosts = async (params) => {
        const { data } = await api.get('/wp/v2/posts', { params });
        data
            .filter(p =>
                !p.categories.includes(CATEGORY_CONTENT_DETAIL) &&
                !p.categories.includes(CATEGORY_LOGO_DETAIL) &&
                !p.categories.includes(CATEGORY_EVENTS) &&
                (!exclude || p.id !== Number(exclude)) &&
                !seen.has(p.id)
            )
            .sort((a, b) => b.id - a.id)
            .forEach(p => { seen.add(p.id); postsResult.push(p); });
    };

    let brandId = 0;
    if (brandSlug) {
        try {
            const { data: cats } = await api.get('/wp/v2/categories', { params: { slug: brandSlug } });
            if (cats.length) brandId = cats[0].id;
        } catch (e) { /* sem categoria para o slug */ }
    }

    if (brandId) {
        const p = baseParams([brandId]);
        if (tags && tags.length) p.tags = tags.join(',');
        await pushPosts(p);
        if (!brandsOnly && postsResult.length < limit) {
            await pushPosts(baseParams([brandId]));
        }
    }

    if (!brandsOnly) {
        if (postsResult.length < limit) {
            const p = baseParams([CATEGORY_CONTENT]);
            if (tags && tags.length) p.tags = tags.join(',');
            await pushPosts(p);
        }
        if (postsResult.length < limit) {
            await pushPosts(baseParams([category === CATEGORY_EVENTS ? CATEGORY_CONTENT : (category || CATEGORY_CONTENT)]));
        }
    }

    const posts = postsResult.slice(0, limit);
    const catTags = await getTagsByCategory(category || CATEGORY_CONTENT);
    const catTagIds = new Set((catTags || []).map(t => t.id));

    const mapped = await Promise.all(posts.map(p => mapPost(p, categories, colors)));
    mapped.forEach(item => {
        item.tagList = (item.tagList || []).filter(t => catTagIds.has(t.id));
    });

    return { data: mapped };
}

const getTagsByCategory = async (categoryId) => {
    const { data } = await api.get('/wp/v2/posts', {
        params: { categories: categoryId, per_page: 100, _fields: 'id,tags' }
    });
    const ids = new Set();
    data.forEach(p => (p.tags || []).forEach(t => ids.add(t)));
    const tags = await getTagsAll();
    return tags.filter(t => ids.has(t.id));
}

export const getEvents = async (month, year) => {
    const { data } = await api.get('/wp/v2/posts', {
        params: { categories: CATEGORY_EVENTS, per_page: 100, _fields: 'id,slug,title,acf' }
    });

    const list = [];
    data.forEach(post => {
        if (!post.acf?.data_hora_do_evento) return;
        const start = parseEventDate(post.acf.data_hora_do_evento);
        if (!start) return;
        const end = post.acf.data_final_evento ? parseEventDate(post.acf.data_final_evento) : start;

        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
            if (d.getMonth() + 1 === month && d.getFullYear() === year) {
                list.push({
                    id: post.id,
                    slug: post.slug,
                    title: post.title?.rendered || '',
                    eventDate: new Date(d),
                    eventFinalDate: end !== start ? new Date(end) : null
                });
            }
        }
    });

    list.sort((a, b) => a.eventDate - b.eventDate);
    return { data: list };
}

export const getEventsByDate = async (dateEvent) => {
    const { data } = await api.get('/wp/v2/posts', {
        params: { categories: CATEGORY_EVENTS, per_page: 100, _fields: 'id,slug,title,content,excerpt,acf' }
    });

    const target = new Date(dateEvent);
    const events = data.filter(post => {
        if (!post.acf?.data_hora_do_evento) return false;
        const start = parseEventDate(post.acf.data_hora_do_evento);
        if (!start) return false;
        const end = post.acf.data_final_evento ? parseEventDate(post.acf.data_final_evento) : start;
        return target >= start && target <= end;
    });

    // O core colocava o post "sticky" (último post da categoria Eventos) em primeiro.
    const sticky = data
        .filter(p => p.sticky)
        .sort((a, b) => new Date(b.date) - new Date(a.date))[0];
    const ordered = sticky ? [sticky, ...events.filter(e => e.id !== sticky.id)] : events;

    const { categories, colors } = await getContext();
    return { data: await Promise.all(ordered.map(p => mapPost(p, categories, colors))) };
}

export const getCategories = async params => {
    return await api.get('/wp/v2/categories', { params });
}

export const getTags = async () => {
    return await api.get('/wp/v2/tags', { params: { per_page: 100, _fields: TAG_FIELDS } });
}

export const getPages = async params => {
    return await api.get('/wp/v2/pages', { params });
}

export const getPageBySlug = async (slug) => {
    const { data } = await api.get('/wp/v2/pages', {
        params: { slug, _fields: 'id,slug,title,content,excerpt,featured_media,acf,elementor_css,elementor_data,_embedded', _embed: true }
    });
    return { data: data.length ? mapPage(data[0]) : null };
}

// Converte "dd/MM/yyyy HH:mm:ss" do ACF em Date
const parseEventDate = (value) => {
    const m = /^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}:\d{2}):\d{2}$/.exec(value);
    if (!m) return null;
    return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]), Number(m[4].split(':')[0]), Number(m[4].split(':')[1]));
}
